"use client";

import { useState, type FormEvent } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

/**
 * Ersetzt den deaktivierten "Bald verfügbar"-Button, solange PAYMENTS_ENABLED false ist
 * (backlog.md -> [Punkt 88]) - sammelt parallel schon Leads fuer den Launch, statt
 * Interessenten ins Leere laufen zu lassen. Nutzt die neue, eigenstaendige
 * lib/waitlist/-Infrastruktur (Netlify Blobs + Resend, gleiches Muster wie
 * lib/leadgate/, siehe dortige Docstrings fuer die Abgrenzung).
 */
export function WaitlistButton({
  tier,
  className,
}: {
  tier: "pro" | "max";
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [error, setError] = useState<string | null>(null);

  function onOpenChange(next: boolean) {
    setOpen(next);
    if (!next) {
      // Zustand zuruecksetzen, damit ein erneutes Oeffnen nicht mit dem alten
      // Erfolgs-/Fehlerzustand startet.
      setEmail("");
      setStatus("idle");
      setError(null);
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setStatus("loading");
    setError(null);
    try {
      const res = await fetch("/api/waitlist/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, tier }),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => null)) as { error?: string } | null;
        setStatus("error");
        setError(data?.error ?? "Anmeldung fehlgeschlagen. Bitte später erneut versuchen.");
        return;
      }
      setStatus("success");
    } catch {
      setStatus("error");
      setError("Anmeldung fehlgeschlagen. Bitte später erneut versuchen.");
    }
  }

  return (
    <>
      <Button type="button" size="sm" className={className} onClick={() => setOpen(true)}>
        Auf die Warteliste setzen
      </Button>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              {status === "success" ? "Du stehst auf der Liste" : "Warteliste"}
            </DialogTitle>
          </DialogHeader>
          {status === "success" ? (
            <p className="text-sm text-ink-secondary">
              Danke für dein Interesse! Sobald Zahlungen freigeschaltet sind, melden wir uns unter
              dieser Adresse als Erstes bei dir.
            </p>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-3">
              <p className="text-sm text-ink-secondary">
                Zahlungen sind aktuell noch nicht freigeschaltet. Trag dich ein, um informiert zu
                werden, sobald es so weit ist.
              </p>
              <Input
                type="email"
                required
                placeholder="deine@email.de"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-label="E-Mail-Adresse"
              />
              {error && <p className="text-sm text-negative">{error}</p>}
              <DialogFooter>
                <Button type="submit" disabled={status === "loading"}>
                  {status === "loading" ? "Wird gesendet…" : "Eintragen"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
